import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock, createVNode as _createVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "login-page" }
const _hoisted_2 = { class: "login-page__shell" }
const _hoisted_3 = { class: "login-form__body" }
const _hoisted_4 = {
  key: 0,
  class: "login-form__welcome"
}
const _hoisted_5 = {
  key: 1,
  class: "login-form__session login-form__session--admin"
}
const _hoisted_6 = { class: "login-form__session-actions" }
const _hoisted_7 = {
  key: 2,
  class: "login-form__session"
}
const _hoisted_8 = { class: "login-form__social" }
const _hoisted_9 = { class: "login-form__field" }
const _hoisted_10 = { class: "login-form__field" }
const _hoisted_11 = { class: "login-form__auto" }
const _hoisted_12 = {
  key: 0,
  class: "login-form__actions"
}
const _hoisted_13 = ["disabled"]
const _hoisted_14 = {
  key: 1,
  class: "login-form__links"
}

import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import KakaoLoginButton from '@/components/auth/KakaoLoginButton.vue'
import PublicLoginKindChoice from '@/components/auth/PublicLoginKindChoice.vue'
import { startKakaoLogin } from '@/composables/useKakaoOAuth'
import { applyMemberHomeAfterLogin, memberHomeAfterLogin } from '@/composables/useLoginDestination'
import { consumeLoginPrefill } from '@/constants/authPrefill'
import { loadAutoLogin } from '@/constants/autoLogin'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { useSellerAuth } from '@/composables/useSellerAuth'
import {
  decidePublicLoginKind,
  identifyPublicLogin,
  preferAdminLogin,
  preferSellerLogin,
  customerPathAfterStaffLogin,
  sellerHomePath,
  type PublicLoginKind,
} from '@/composables/useUnifiedLogin'
import {
  inputValidationState,
  isLoginPasswordValid,
  isLoginUserIdValid,
  sanitizeLoginUserIdInput,
} from '@/utils/formValidation'


export default /*@__PURE__*/_defineComponent({
  __name: 'LoginPage',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const { login: loginMember, isLoggedIn, initMemberAuth } = useMemberAuth()
const { admin, isAdminLoggedIn, initAdminAuth, login: loginAdmin } = useAdminAuth()
const {
  login: loginSeller,
  logout: logoutSeller,
  seller,
  isSellerLoggedIn,
  initSellerAuth,
} = useSellerAuth()

const form = reactive({
  userId: '',
  password: '',
})

const isSubmitting = ref(false)
const kakaoBusy = ref(false)
const rememberMe = ref(loadAutoLogin('member') || loadAutoLogin('seller') || loadAutoLogin('admin'))
const forceSellerRelogin = ref(false)
const forceMemberLogin = ref(false)
const pendingKinds = ref<PublicLoginKind[] | null>(null)

const justJoined = computed(() => route.query.joined === '1')
const preferSeller = computed(() => preferSellerLogin(route.query))
const preferAdmin = computed(() => preferAdminLogin(route.query))
const existingSeller = computed(() => (isSellerLoggedIn.value ? seller.value : null))
const existingAdmin = computed(() => (isAdminLoggedIn.value ? admin.value : null))
const showAdminSession = computed(() => {
  if (!existingAdmin.value || forceMemberLogin.value) return false
  const next = redirectPath.value
  /* 몰·고객 화면으로 가려다 온 경우에는 관리 화면 입장을 띄우지 않는다. */
  if (next && !next.startsWith('/admin')) return false
  return true
})
const showSellerSession = computed(
  () => Boolean(existingSeller.value) && preferSeller.value && !forceSellerRelogin.value,
)
const showLoginFields = computed(() => !showAdminSession.value && !showSellerSession.value)
const needsKindChoice = computed(() => Boolean(pendingKinds.value))
const onUserIdInput = (event: Event) => {
  form.userId = sanitizeLoginUserIdInput((event.target as HTMLInputElement).value)
  pendingKinds.value = null
}

const userIdInputState = computed(() =>
  inputValidationState(form.userId, isLoginUserIdValid(form.userId)),
)

const passwordInputState = computed(() =>
  inputValidationState(form.password, isLoginPasswordValid(form.password)),
)

const onSellerRelogin = async () => {
  forceSellerRelogin.value = true
  form.userId = ''
  form.password = ''
  pendingKinds.value = null
  try {
    await logoutSeller()
  } catch {
    /* 세션이 이미 끝났어도 입력 폼은 연다 */
  }
}

const redirectPath = computed(() =>
  typeof route.query.redirect === 'string' ? route.query.redirect : '',
)

const goAdminHome = async () => {
  const next = redirectPath.value
  const stay =
    next.startsWith('/admin') && !next.startsWith('/admin/login') ? next : '/admin'
  await router.push(stay)
}

const redirectAfterMemberLogin = async () => {
  await router.push(redirectPath.value || applyMemberHomeAfterLogin())
}

const redirectAfterSellerLogin = async () => {
  await router.push(sellerHomePath(seller.value, redirectPath.value))
}

const redirectAfterStaffLogin = async () => {
  await router.push(customerPathAfterStaffLogin(redirectPath.value))
}

const onKakaoLogin = async () => {
  if (kakaoBusy.value || isSubmitting.value) {
    return
  }
  kakaoBusy.value = true
  try {
    await startKakaoLogin({
      redirect: redirectPath.value || memberHomeAfterLogin(),
      remember: rememberMe.value,
    })
  } catch (error) {
    kakaoBusy.value = false
    window.alert(error instanceof Error ? error.message : '카카오 로그인을 시작하지 못했습니다.')
  }
}

async function enterAs(kind: PublicLoginKind) {
  if (kind === 'seller') {
    await loginSeller(form.userId, form.password, rememberMe.value)
    await redirectAfterStaffLogin()
    return
  }
  if (kind === 'admin') {
    await loginAdmin(form.userId, form.password, rememberMe.value)
    await redirectAfterStaffLogin()
    return
  }
  await loginMember(form.userId, form.password, rememberMe.value)
  await redirectAfterMemberLogin()
}

const onPickKind = async (kind: PublicLoginKind) => {
  if (isSubmitting.value) {
    return
  }
  isSubmitting.value = true
  try {
    await enterAs(kind)
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '로그인에 실패했습니다.')
  } finally {
    isSubmitting.value = false
  }
}

const onResetKind = () => {
  pendingKinds.value = null
}

const onSubmit = async () => {
  if (isSubmitting.value || needsKindChoice.value) {
    return
  }

  if (!isLoginUserIdValid(form.userId)) {
    window.alert('아이디 형식을 확인해 주세요.')
    return
  }

  if (!isLoginPasswordValid(form.password)) {
    window.alert('비밀번호를 입력해 주세요.')
    return
  }

  isSubmitting.value = true

  try {
    const kinds = await identifyPublicLogin(form.userId, form.password)
    const decision = decidePublicLoginKind(kinds, preferSeller.value, preferAdmin.value)
    if (decision === 'choose') {
      pendingKinds.value = kinds
      return
    }
    await enterAs(decision)
  } catch (error) {
    const message = error instanceof Error ? error.message : '로그인에 실패했습니다.'
    window.alert(message)
  } finally {
    isSubmitting.value = false
  }
}

onMounted(async () => {
  const shouldCheckSeller =
    preferSeller.value || Boolean(seller.value) || loadAutoLogin('seller')

  await Promise.all([
    initMemberAuth(),
    initAdminAuth(),
    shouldCheckSeller || redirectPath.value.startsWith('/mall')
      ? initSellerAuth()
      : Promise.resolve(),
  ])

  if (
    redirectPath.value.startsWith('/mall') &&
    !redirectPath.value.startsWith('/mall/signup') &&
    (isAdminLoggedIn.value || isSellerLoggedIn.value)
  ) {
    await router.replace(redirectPath.value)
    return
  }

  if (preferSeller.value) {
    const prefill = consumeLoginPrefill()
    if (prefill) {
      form.userId = prefill.userId
      form.password = prefill.password
    }
    return
  }

  if (isLoggedIn.value && !preferAdmin.value) {
    await redirectAfterMemberLogin()
    return
  }

  const prefill = consumeLoginPrefill()
  if (!prefill) {
    return
  }

  form.userId = prefill.userId
  form.password = prefill.password
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[15] || (_cache[15] = _createElementVNode("header", { class: "login-page__head" }, [
        _createElementVNode("p", { class: "login-page__kicker" }, "계정"),
        _createElementVNode("h1", { class: "login-page__title" }, "로그인")
      ], -1)),
      _createElementVNode("form", {
        class: "login-page__panel login-form",
        onSubmit: _withModifiers(onSubmit, ["prevent"])
      }, [
        _createElementVNode("div", _hoisted_3, [
          (justJoined.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_4, " 회원가입이 완료되었습니다. 아이디와 비밀번호를 채워 두었습니다. 로그인만 하시면 됩니다. "))
            : _createCommentVNode("", true),
          (showAdminSession.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("p", null, [
                  _createElementVNode("strong", null, _toDisplayString(existingAdmin.value?.name || existingAdmin.value?.userId), 1),
                  _cache[4] || (_cache[4] = _createTextVNode(" 관리자로 이미 들어가 있습니다. ", -1))
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "login-form__submit",
                    onClick: goAdminHome
                  }, " 관리 화면 입장 "),
                  _createElementVNode("button", {
                    type: "button",
                    class: "login-form__ghost",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (forceMemberLogin.value = true))
                  }, " 다른 계정으로 로그인 ")
                ])
              ]))
            : _createCommentVNode("", true),
          (showSellerSession.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("p", null, [
                  _createElementVNode("strong", null, _toDisplayString(existingSeller.value.storeName), 1),
                  _cache[5] || (_cache[5] = _createTextVNode(" 판매업체로 이미 로그인되어 있습니다. ", -1))
                ]),
                _createElementVNode("div", { class: "login-form__session-actions" }, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "login-form__submit",
                    onClick: redirectAfterSellerLogin
                  }, " 판매관리센터 입장 "),
                  _createElementVNode("button", {
                    type: "button",
                    class: "login-form__ghost",
                    onClick: onSellerRelogin
                  }, " 다른 계정으로 로그인 ")
                ])
              ]))
            : _createCommentVNode("", true),
          (showLoginFields.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                (needsKindChoice.value)
                  ? (_openBlock(), _createBlock(PublicLoginKindChoice, {
                      key: 0,
                      kinds: pendingKinds.value || [],
                      busy: isSubmitting.value,
                      onPick: onPickKind,
                      onReset: onResetKind
                    }, null, 8, ["kinds", "busy"]))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createElementVNode("div", _hoisted_8, [
                        _createVNode(KakaoLoginButton, {
                          busy: kakaoBusy.value,
                          onClick: onKakaoLogin
                        }, null, 8, ["busy"]),
                        _cache[6] || (_cache[6] = _createElementVNode("p", { class: "login-form__split" }, "또는 아이디로 로그인", -1))
                      ]),
                      _createElementVNode("label", _hoisted_9, [
                        _cache[7] || (_cache[7] = _createElementVNode("span", null, "아이디", -1)),
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.userId) = $event)),
                          type: "text",
                          autocomplete: "username",
                          placeholder: "아이디를 입력해 주세요",
                          maxlength: "40",
                          class: _normalizeClass(userIdInputState.value),
                          onInput: onUserIdInput
                        }, null, 34), [
                          [_vModelText, form.userId]
                        ])
                      ]),
                      _createElementVNode("label", _hoisted_10, [
                        _cache[8] || (_cache[8] = _createElementVNode("span", null, "비밀번호", -1)),
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.password) = $event)),
                          type: "password",
                          autocomplete: "current-password",
                          placeholder: "비밀번호를 입력해 주세요",
                          class: _normalizeClass(passwordInputState.value)
                        }, null, 2), [
                          [_vModelText, form.password]
                        ])
                      ]),
                      _createElementVNode("label", _hoisted_11, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((rememberMe).value = $event)),
                          type: "checkbox"
                        }, null, 512), [
                          [_vModelCheckbox, rememberMe.value]
                        ]),
                        _cache[9] || (_cache[9] = _createElementVNode("span", null, "자동 로그인", -1)),
                        _cache[10] || (_cache[10] = _createElementVNode("em", null, "공용 PC에서는 사용하지 마세요", -1))
                      ])
                    ], 64))
              ], 64))
            : _createCommentVNode("", true)
        ]),
        (showLoginFields.value && !needsKindChoice.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
              _createElementVNode("button", {
                type: "submit",
                class: "login-form__submit",
                disabled: isSubmitting.value
              }, _toDisplayString(isSubmitting.value ? '로그인 중...' : '로그인'), 9, _hoisted_13),
              _createVNode(_component_router_link, {
                to: "/signup",
                class: "login-form__ghost login-form__signup"
              }, {
                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                  _createTextVNode(" 회원가입 ", -1)
                ]))]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true),
        (showLoginFields.value && !needsKindChoice.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createVNode(_component_router_link, {
                to: "/find-id",
                class: "login-form__link"
              }, {
                default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                  _createTextVNode("아이디 찾기", -1)
                ]))]),
                _: 1
              }),
              _cache[14] || (_cache[14] = _createElementVNode("span", {
                class: "login-form__divider",
                "aria-hidden": "true"
              }, null, -1)),
              _createVNode(_component_router_link, {
                to: "/find-password",
                class: "login-form__link"
              }, {
                default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                  _createTextVNode("비밀번호 찾기", -1)
                ]))]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true)
      ], 32)
    ])
  ]))
}
}

})